import * as Sentry from '@sentry/react';
import { useEffect } from 'react';

import { useAppSelector } from '@/store';

type MissingProjectSurface = 'expert_request_page' | 'consultation_page';
type MissingProjectReason = 'project_absent' | 'project_null';

// One report per surface, reason and expert request per page load. `connect` re-runs the
// selector on every dispatch, so the ER detail page re-renders many times per visit —
// without this, a single stuck node would emit an event per render. Keyed on surface and
// reason as well as the id, like `reportUnresolvableChatUser`, so a report from one page
// does not swallow a different finding about the same expert request on the other.
const reportedMissingProjects = new Set<string>();

// Exported for tests only — the module-level dedup would otherwise leak between cases.
export function resetMissingProjectReports(): void {
  reportedMissingProjects.clear();
}

/**
 * Reports an expert request that reached render carrying no project.
 *
 * ENG-5787 stopped this from throwing, which is right — a crashed page helps nobody — but
 * silence would be wrong here, because the condition is not the benign one the guard makes
 * it look like. All three expert requests in WEBAPP-CDV / CDW / CDK (9545, 9363, 9555) have
 * live, readable projects, so the absent project was never "this ER has no project": the
 * Redux node had lost the key. The error text says so too — `undefined`, not `null`, so the
 * key was absent rather than resolved-to-null.
 *
 * `reason` keeps the two causes separable, because neither is visible any other way and
 * they have different fixes:
 *   - `project_absent`: the key is missing. The node in the shared `expertRequests.default`
 *     bucket was evicted and re-minted as a stub (`{ id }` plus candidates, no project) —
 *     see the `expertRequestIndex < 0` branch in `store/reducer.ts`. A stub is truthy, so
 *     it slips past the `expertRequest` guards and only surfaces on the first field the
 *     request does not carry.
 *   - `project_null`: the api resolved `project` to null, which it does for a missing
 *     project AND for a denied `project:view` (expertrequest_type_gql.go folds both into
 *     `nil, nil`). A denial is an ordinary answer for some viewers: a team `member` on a team
 *     whose project visibility is not `all` can view its expert requests but not their
 *     projects (expertrequest_policies.go vs project_policies.go). So this is only reported
 *     for admins, whom the staff policy grants every permission — for them null can only
 *     mean the project itself is gone.
 *
 * Level `warning`, matching `reportUnresolvableChatUser` in `src/messaging/utils.ts`: the
 * page renders and the user is not blocked, so this is searchable, not paged on. A real
 * event rather than a breadcrumb for the same reason given there — nothing throws any more,
 * so there is no later error for a breadcrumb to ride on.
 */
export function reportMissingProject(
  surface: MissingProjectSurface,
  reason: MissingProjectReason,
  expertRequestId: string | undefined
): void {
  const id = expertRequestId ?? 'unknown';
  const key = `${surface}:${reason}:${id}`;
  if (reportedMissingProjects.has(key)) return;
  reportedMissingProjects.add(key);

  Sentry.captureMessage('Expert request reached render with no project', {
    level: 'warning',
    tags: { area: 'expert_request', reason, surface },
    // Opaque expert request id only — no project, group or member data.
    extra: { expertRequestId: id },
  });
}

/**
 * Reports once the expert request is loaded and its project is missing. Pass `undefined`
 * while there is nothing to check, or for a viewer the check does not apply to. A null
 * project is only reported when the viewer is an admin (see `project_null` above), read here
 * rather than passed in so no caller can leave it out. Run from an effect so the dedup above
 * guards remounts rather than every render.
 */
export function useReportMissingProject(
  surface: MissingProjectSurface,
  expertRequest: { id?: string; project?: unknown } | null | undefined
): void {
  const viewerIsAdmin = useAppSelector((state) => !!state.viewer.admin);
  const id = expertRequest?.id;
  let reason: MissingProjectReason | undefined;
  if (expertRequest && expertRequest.project === undefined) reason = 'project_absent';
  if (expertRequest && expertRequest.project === null && viewerIsAdmin) reason = 'project_null';

  useEffect(() => {
    if (reason) reportMissingProject(surface, reason, id);
  }, [surface, reason, id]);
}
